import React, { useEffect, useState } from 'react';
import { NavLink, Link } from 'react-router-dom';

import constants from '../lib/constants';
import api from '../utils/api';
import Loader from '../lib/components/Loader';
import { projectDraft } from '../lib/draftProjection';
import ProjectedPlayer from '../lib/components/ProjectedPlayer';


const Draft = ({ playerData, outgoingPicks }) => {   
    const FullDraft = ({ year }) => {
        const [draftOrder, updateDraftOrder] = useState(null);
        const [isProjected, updateIsProjected] = useState(true);
        const [loading, updateLoading] = useState(false);
        const [error, updateError] = useState(false);
    
        useEffect(() => {
            updateLoading(true);
            api.get(`/api/v1/draft/${year}/order`).then(({ data: {order, isProjected} }) => {
                updateDraftOrder(order);
                updateIsProjected(isProjected);
            }).catch(res => {
                console.error(res);
                updateError(true);
            }).finally(() => {
                updateLoading(false);
            })
        }, [year]);
    
        if (loading) {
            return <Loader/>;
        }
    
        if (error || !draftOrder) {
            return <div>Something went wrong loading draft order!</div>;
        }
    
        // picks
        const { pool: draftPlayers, tags, taggedIds, picks } = projectDraft({
                playerData,
                year,
                currentSeason: constants.currentSeason,
                order: draftOrder,
                outgoingPicks,
            }),
            // Round 1 projection by player id, for the "Projection" column.
            pickByPlayerId = new Map(picks.filter(p => p.player && !p.player.isRookie).map(p => [p.player.id, p])),
            tagByPlayerId = new Map(tags.map(t => [t.player.id, t.manager])),
            fullDraft = [];
    
        fullDraft.push.apply(fullDraft, draftOrder); // 1
        fullDraft.push.apply(fullDraft, draftOrder); // 2
        fullDraft.push.apply(fullDraft, draftOrder); // 3
        fullDraft.push.apply(fullDraft, draftOrder); // 4
        fullDraft.push.apply(fullDraft, draftOrder); // 5
        fullDraft.push.apply(fullDraft, draftOrder); // 6
        fullDraft.push.apply(fullDraft, draftOrder); // 7
    
        return (
            <div className="sectionContainer">
                <h3>{year} Draft</h3>
                <div>
                    <h4>Draft Picks</h4>
                    <div className="d-grid mx-auto">
                        <button className="btn btn-outline-info draft-accordion-btn" type="button" data-bs-toggle="collapse" data-bs-target={'#draft-table' + year} aria-expanded="false" aria-controls={'draft-table' + year}>
                            <i className="bi bi-chevron-down me-2"></i>
                            Show/Hide Draft Order
                            <i className="bi bi-list-ul ms-2"></i>
                        </button>
                    </div>
                    <div className="collapse" id={'draft-table' + year}>
                        <table className="table table-dark table-bordered table-striped caption-top">
                            {isProjected && <caption>*Projected Order</caption>}
                            <thead>
                                <tr>
                                    <th scope="col">#</th>
                                    <th scope="col" style={{}}>Manager</th>
                                </tr>
                            </thead>
                            <tbody>
                                {fullDraft.map((manager, index) => {
                                    // let origPickManager = draftData.order[index % 12]
                                    let roundNumber = Math.floor(index/12),
                                        newPickOwner;
    
                                    //find if traded pick
                                    if (outgoingPicks[manager] && outgoingPicks[manager][year]) {
                                        newPickOwner = outgoingPicks[manager][year][roundNumber + 1];
                                    }
                                    return (
                                        <tr key={manager + index} style={{lineHeight: .5}}>
                                            <th scope="row">{roundNumber + 1}.{index + 1}</th>
                                            <td style={{}}>{manager} {newPickOwner ? <span style={{fontWeight: "bold"}}>({newPickOwner})</span> : ''}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
                <div>
                    <h4>Projected Franchise Tags</h4>
                    <p className="text-light opacity-75 small">
                        Estimate: each manager tags their highest-ranked player on an expiring deal who
                        hasn't been tagged before. Tagged players come off the board below.
                    </p>
                    {tags.length === 0 ? <p>No tag-eligible players.</p> : (
                        <table className="table table-dark table-bordered table-striped">
                            <thead>
                                <tr>
                                    <th scope="col">Manager</th>
                                    <th scope="col">Player</th>
                                    <th scope="col" style={{textAlign: "right"}}>Y! Rank</th>
                                </tr>
                            </thead>
                            <tbody>
                                {tags.map(({ manager, player }) => (
                                    <tr key={manager} style={{lineHeight: .5}}>
                                        <td>{manager}</td>
                                        <td><Link to={`/player/${player.id}`}>{player.name}</Link> | {player.posname}</td>
                                        <td style={{textAlign: "right"}}>{player.yahooleaguerank || '-'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
                <div>
                    <h4>Projected Round 1</h4>
                    <p className="text-light opacity-75 small">
                        The best remaining players by rank, after the estimated tags. Rookies usually go
                        two or three deep in round 1, so unnamed rookie placeholders hold a few picks.
                    </p>
                    <table className="table table-dark table-bordered table-striped">
                        <thead>
                            <tr>
                                <th scope="col">Pick</th>
                                <th scope="col">Manager</th>
                                <th scope="col">Projected player</th>
                            </tr>
                        </thead>
                        <tbody>
                            {picks.map(({ pick, manager, tradedTo, player }) => (
                                <tr key={pick} style={{lineHeight: .5}}>
                                    <th scope="row">1.{pick}</th>
                                    <td>{manager}{tradedTo ? <strong> ({tradedTo})</strong> : ''}</td>
                                    <td><ProjectedPlayer player={player} /></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <div>
                    <h4>Available Players</h4>
                    <table className="table table-dark table-bordered table-striped">
                        <thead>
                            <tr>
                                <th scope="col">Rank</th>
                                <th scope="col">Player</th>
                                <th scope="col" style={{textAlign: "right"}}>Can Tag?</th>
                                <th scope="col" style={{textAlign: "right"}}>Projection</th>
                            </tr>
                        </thead>
                        <tbody>
                            {draftPlayers.map((player, index) => {
                                let canTag = player.manager && !player.franchisetag;
                                const pick = pickByPlayerId.get(player.id);
                                return (
                                    <tr key={player + index} style={{lineHeight: .5}} className={canTag ? 'tag-eligible-row' : ''}>
                                        <th scope="row">{index + 1}</th>
                                        <td><Link to={`/player/${player.id}`}>{player.name}</Link> | {player.posname}</td>
                                        <td style={{textAlign: "right"}}>{canTag ? player.manager : '-'}</td>
                                        <td style={{textAlign: "right"}}>
                                            {taggedIds.has(player.id)
                                                ? <span className="badge bg-warning text-dark">Tag: {tagByPlayerId.get(player.id)}</span>
                                                : pick
                                                    ? <span>1.{pick.pick} {pick.manager}{pick.tradedTo ? <strong> ({pick.tradedTo})</strong> : ''}</span>
                                                    : ''}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        )
    };
    return (
        <div id="DraftPageContainer">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h2>Draft Central</h2>
                <NavLink to="/history" className="btn btn-outline-primary">
                    View Past Drafts
                    <i className="bi bi-chevron-right ms-2"></i>
                </NavLink>
            </div>
            <FullDraft year={2027}/>
            <FullDraft year={2028}/>
        </div>
    );
}

export default Draft;